/* eslint-disable */
import {
  EyeOutlined, ImportOutlined, PlusOutlined,
  RollbackOutlined
} from '@ant-design/icons';
import {
  Button, Card, message, Modal, Space,
  Tooltip,
  Typography
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { AxiosError } from 'axios';
import React, {
  useEffect, useMemo, useRef, useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import DetailModal from './detailModal';

import { useAppSelector } from 'app/store';
import CreateEmployee, { CreateStaffRef } from 'common/components/CreateEmployee';
import PageTable from 'common/components/PageTable';
import { approveStaffProjectService, addAndApproveStaffProjectService, getStaffFromPayRallProjectService } from 'common/services/extends/project';
import { ROUTE_PATHS } from 'common/utils/constant';
import { detectError, detectErrorByCode } from 'common/utils/functions';
import roles, { getPermission } from 'configs/roles';
import { ApproveStaffParams } from 'common/services/extends/project/types';
import { PayrollPeriodsDataTypes } from 'common/services/extends/payrollPeriods/types';

type ColumnType = {
  id: number;
  name: string;
  idCardNumber: string;
};

interface ApprovedStaffsProps {
  projectData: {
    name: string
    customerName: string
    code: string
    payrollName: string
  }
  isOpenLink: boolean
  disabledAction?: boolean;
  payrollData?: PayrollPeriodsDataTypes
}

const ApprovedStaffs: React.FC<ApprovedStaffsProps> = ({
  projectData,
  isOpenLink,
  disabledAction,
  payrollData
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = Number(searchParams.get('page'));
  const projectId = searchParams.get('projectId') || '';
  const staffFullTimeId = searchParams.get('staffFullTimeId') || '';
  const payrollPeriodId = searchParams.get('payrollPeriodId') || '';
  const queryClient = useQueryClient();
  const { roles: userRoles } = useAppSelector((state) => state.auth);
  const {
    defaultPageSize,
  } = useAppSelector((state) => state.system);

  /* Ref */
  const formRef = useRef<CreateStaffRef>(null);

  /* States */
  const [currentPage, setCurrentPage] = useState(Number(pageParam || 1));
  const [keyword, setKeyword] = React.useState<string>('');
  const [isShowPopupCreateStaff, setShowPopupCreateStaff] = React.useState<boolean>(false);
  const [openId, setOpenId] = React.useState<number | undefined>(undefined);

  const queryApproveStaff = ['get-approved-staffs-project', staffFullTimeId, currentPage, keyword, defaultPageSize];

  /* Query */
  const { data: staffRes, isLoading } = useQuery(
    queryApproveStaff,
    () => {
      if (payrollPeriodId) {
        return getStaffFromPayRallProjectService(payrollPeriodId, {
          page: currentPage,
          limit: defaultPageSize,
          approved: true,
          ...keyword && { keyword },
        });
      }
      return null;
    },
    {
      enabled: !!(payrollPeriodId) && getPermission(userRoles, roles.PROJECT_INDEX),
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          message.error(t(detectError(status)));
        }
      }
    }
  );

  const { mutate: addAndApproveMutate, isLoading: isLoadingAddAndApprove } = useMutation(
    ['add-approve-staffs'],
    (params: {
      payrollPeriodId: string,
      params: { idCardNumber: string }
    }) => addAndApproveStaffProjectService(params.payrollPeriodId, params.params),
    {
      onSuccess: () => {
        message.success(t('message.addAndApproveStaffSuccess'));
        queryClient.invalidateQueries(queryApproveStaff);
        if (formRef.current) formRef.current.handleReset();
      },
      onError: (error: ErrorResponse[]) => {
        if (error.length > 0) {
          error.forEach((
            e
          ) => {
            message.error(
              t(detectErrorByCode(e.code, e.field, e.message))
            );
          });
        } else {
          message.error(t('message.anErrorHasOccurred'));
        }
      }
    }
  );

  const formatStaff: ColumnType[] = useMemo(() => staffRes?.data.map((item) => ({
    id: item.staffData.id,
    name: item.staffData.fullname,
    idCardNumber: item.staffData.idCardNumber,
  })) || [], [staffRes]);

  const { mutate: removeMutate, isLoading: isLoadingRemove } = useMutation(
    ['remove-staffs'],
    (params: ApproveStaffParams) => approveStaffProjectService(params),
    {
      onSuccess: () => {
        message.success(t('message.rejectStaffSuccess'));
        queryClient.invalidateQueries(['get-waiting-staffs-project']);
        queryClient.invalidateQueries(queryApproveStaff);
        setOpenId(undefined);
      },
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          message.error(t(detectError(status)));
        }
      }
    }
  );

  const handleRevokeSingle = (index: number) => {
    removeMutate({
      payrollPeriodId,
      staffIds: [index],
      approved: false,
    });
  };
  const handleRevoke = (ids: number[]) => {
    removeMutate({
      payrollPeriodId,
      staffIds: ids,
      approved: false,
    });
  };

  const handleClosePopup = () => {
    setShowPopupCreateStaff(false);
    if (formRef.current) formRef.current.handleReset();
  };

  const columns: ColumnsType<ColumnType> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: t('system.name'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: t('program.idCardNumber'),
      dataIndex: 'idCardNumber',
      key: 'idCardNumber',
      width: 200,
    },
    {
      title: t('system.action'),
      key: 'action',
      dataIndex: 'action',
      width: 100,
      align: 'center',
      render: (_, data) => (
        <Space>
          <Tooltip title={t('program.seeDetailEmployee')}>
            <Button
              icon={<EyeOutlined />}
              onClick={() => setOpenId(data.id)}
            />
          </Tooltip>
          {
            !disabledAction && (
              <Tooltip title={t('program.revoke')}>
                <Button
                  size="small"
                  danger
                  disabled={!getPermission(userRoles, roles.PROJECT_APPROVED_STAFF)}
                  onClick={() => Modal.confirm({
                    className: 't-pagetable_deleteRecordModal',
                    autoFocusButton: 'cancel',
                    okText: t('system.ok'),
                    cancelText: t('system.cancel'),
                    cancelButtonProps: {
                      type: 'primary',
                    },
                    okButtonProps: {
                      type: 'default',
                    },
                    title: t('message.confirmDeleteStaff'),
                    onOk: () => {
                      handleRevoke?.([data.id]);
                    },
                  })}
                >
                  <RollbackOutlined />
                </Button>
              </Tooltip>
            )
          }
        </Space>
      ),
    }
  ];

  const handleSetCurrentPage = (page: number) => {
    setCurrentPage(page);
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set('page', String(page));
    setSearchParams(newSearchParams);
  };

  useEffect(() => {
    if (pageParam) {
      setCurrentPage(Number(pageParam));
    } else {
      setCurrentPage(1);
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.set('page', '1');
      setSearchParams(newSearchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card
      style={{ height: '100%' }}
      title={(
        <Space size={0} style={{ width: '100%', justifyContent: 'space-between' }}>
          <Typography.Title level={4}>
            {t('program.approvedStaffs')}
          </Typography.Title>
          <Space>
            <Button
              disabled={!isOpenLink || disabledAction || !getPermission(userRoles, roles.PROJECT_IMPORT_STAFF)}
              onClick={() => navigate(`${ROUTE_PATHS.PROJECT_EMPLOYEE_IMPORT}?projectId=${projectId}&staffFullTimeId=${staffFullTimeId}&payrollPeriodId=${payrollPeriodId}`)}
            >
              <ImportOutlined />
              {t('program.import')}
            </Button>
            <Button
              type="primary"
              disabled={!isOpenLink || disabledAction || !getPermission(userRoles, roles.PROJECT_APPROVED_STAFF)}
              onClick={() => setShowPopupCreateStaff(true)}
            >
              <PlusOutlined />
              {t('program.addStaff')}
            </Button>
          </Space>
        </Space>
      )}
    >
      <div className="p-program_approvedStaffs">
        <PageTable
          noCheckbox
          isLoading={isLoading || isLoadingRemove || isLoadingAddAndApprove}
          handleSearch={(k) => setKeyword(k)}
          tableProps={{
            columns,
            pageData: formatStaff,
            currentPage,
            pageSize: defaultPageSize,
            total: staffRes?.meta.total,
            noBaseCol: true,
            noDeleteLanguage: true,
            initShowColumns: ['name', 'idCardNumber', 'action'],
            handleSetCurrentPage,
            scroll: { x: 'auto' },
          }}
        />
      </div>
      <Modal
        open={isShowPopupCreateStaff}
        width={1280}
        title={<Typography.Title level={3}>{t('program.addStaffTitle')}</Typography.Title>}
        onCancel={handleClosePopup}
        footer={null}
      >
        <CreateEmployee
          roleIndex={getPermission(userRoles, roles.EMPLOYEE_INDEX)}
          ref={formRef}
          program={{
            info: projectData,
            handleClosePopup: (idCardNumber) => {
              setShowPopupCreateStaff(false);
              addAndApproveMutate({
                payrollPeriodId,
                params: {
                  idCardNumber
                }
              });
            },
            loading: isLoadingAddAndApprove
          }}
          payrollData={payrollData}
        />
      </Modal>
      <DetailModal
        openId={openId}
        handleClose={() => setOpenId(undefined)}
        handleReject={(index) => handleRevokeSingle(index)}
        isApproved
        disabledAction={disabledAction}
      />
    </Card>
  );
};
export default ApprovedStaffs;
